import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../../styles/components/landing/ForProfessionals.css';

// Import icons from assets
import brainIcon from '../../../assets/icons/landing/brain_icon.png';
import stethoscopeIcon from '../../../assets/icons/landing/docTool_icon.png';
import journalIcon from '../../../assets/icons/landing/journal_icon.png';
import lockIcon from '../../../assets/icons/landing/lock_icon.png';

const ForProfessionals = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);

  const handleRegisterClick = () => {
    navigate('/doctor-register');
  };

  // Your Purpose checklist items
  const purposeItems = [
    "Valid professional license in mental health",
    "Relevant specialization or certification", 
    "Verified professional credentials",
    "Demonstrated clinical experience"
  ];

  // Process steps
  const processSteps = [
    {
      number: "01",
      title: "Submit Application",
      description: "Application review typically takes 2-3 business days"
    },
    {
      number: "02", 
      title: "Credential Review",
      description: "Our team verifies your professional qualifications"
    },
    {
      number: "03",
      title: "Account Activation", 
      description: "Start connecting with patients and managing care"
    }
  ];

  // Auto-rotate current step every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % processSteps.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [processSteps.length]);

  return (
    <div className="forprof-page-wrapper">
      {/* Header Section - Centered */}
      <section className="forprof-header-section">
        <div className="forprof-header-container">
          <h1 className="forprof-header-title">
            Welcome, Mental Health Professionals
          </h1>
          <p className="forprof-header-subtitle">
            Join our platform to provide professional care and access advanced patient management tools
          </p>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="forprof-main-section">
        <div className="forprof-main-container">
          {/* Left Content - Your Role on Neurolex */}
          <div className="forprof-main-content">
            <div className="forprof-role-section-content">
              <h2 className="forprof-role-section-title">Your Role on Neurolex</h2>
              
              <div className="forprof-role-grid">
                {/* Your Purpose */}
                <div className="forprof-role-card">
                  <div className="forprof-role-icon">
                    <img src={brainIcon} alt="Purpose" />
                  </div>
                  <div className="forprof-role-content">
                    <h3 className="forprof-role-title">Your Purpose</h3>
                    <p className="forprof-role-description">
                      As a licensed provider, your role is to lead each patient's mental health journey with compassion, expertise, and ethical care.
                    </p>
                  </div>
                </div>

                {/* Your Focus */}
                <div className="forprof-role-card">
                  <div className="forprof-role-icon">
                    <img src={stethoscopeIcon} alt="Focus" />
                  </div>
                  <div className="forprof-role-content">
                    <h3 className="forprof-role-title">Your Focus</h3>
                    <p className="forprof-role-description">
                      You interpret patient details, assess patient needs, and make informed clinical decisions based on evolving insights and session dynamics.
                    </p>
                  </div>
                </div>

                {/* Your Tools */}
                <div className="forprof-role-card">
                  <div className="forprof-role-icon">
                    <img src={journalIcon} alt="Tools" />
                  </div>
                  <div className="forprof-role-content">
                    <h3 className="forprof-role-title">Your Tools</h3>
                    <p className="forprof-role-description">
                      Neurolex offers AI journal insights, customizable journal templates, and secure communication—built to support your workflow.
                    </p>
                  </div>
                </div>

                {/* Your Impact */}
                <div className="forprof-role-card">
                  <div className="forprof-role-icon">
                    <img src={lockIcon} alt="Impact" />
                  </div>
                  <div className="forprof-role-content">
                    <h3 className="forprof-role-title">Your Impact</h3>
                    <p className="forprof-role-description">
                      Technology stays in the background while you lead care-bringing human connection, clinical insight, and real progress.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Right Content - Ready to Join Card */}
          <div className="forprof-main-card">
            <div className="forprof-card-icon">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <path d="M20 2L26 14H38L28 22L32 36L20 28L8 36L12 22L2 14H14L20 2Z" fill="#4CAF50"/>
              </svg>
            </div>
            
            <div className="forprof-card-content">
              <h3 className="forprof-card-title">Ready to Join?</h3>
              <p className="forprof-card-description">
                Register as a verified mental health professional on Neurolex to join a trusted network and deliver care through a secure, data-driven platform.
              </p>
              
              <div className="forprof-checklist">
                <h4 className="forprof-checklist-title">Your Purpose</h4>
                {purposeItems.map((item, index) => (
                  <div key={index} className="forprof-checklist-item">
                    <div className="forprof-checklist-icon">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M13.5 4.5L6 12L2.5 8.5" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              
              <div className="forprof-process-steps">
                {processSteps.map((step, index) => (
                  <div 
                    key={index} 
                    className={`forprof-process-step ${index === currentStep ? 'active' : ''}`}
                  >
                    <div className="forprof-step-number">{step.number}</div>
                    <div className="forprof-step-content">
                      <div className="forprof-step-title">{step.title}</div>
                      <div className="forprof-step-description">{step.description}</div>
                    </div>
                  </div>
                ))}
              </div>
              
              <button 
                onClick={handleRegisterClick}
                className="forprof-register-button"
              >
                Register as a Mental Health Professional
              </button>
              
              <p className="forprof-review-note">
                Application review typically takes 2-3 business days
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForProfessionals;